import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  MapPinIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import VisibilityToggle from "../ui/VisibilityToggle";
import LocationPrecisionToggle from "../ui/LocationPrecisionToggle";
import ToggleSwitch from "./settings/fields/ToggleSwitch";
import { useSettings } from "../contexts/SettingsContext";
import { useReverseGeocode } from "../hooks/useReverseGeocode";
import { formatAddressForApproximateDisplay } from "../utils/geocoding";
import DropdownMenuPortal from "../ui/DropdownMenuPortal";
import Spinner from "../ui/Spinner";
import { logError } from "../utils/logger";
import {
  normalizeError,
  ValidationError,
  NetworkError,
  ServerError,
} from "../utils/apiErrors";
import ErrorBanner from "../ui/ErrorBanner";
import ImageCropModal from "./notes/ImageCropModal";
import { MAX_NOTE_IMAGES } from "../utils/constants";

// Map backend field names (Mongoose validation error keys) onto the form
// fields this modal actually renders. Any backend field not listed here
// has no corresponding input, so its error falls back to the top-level banner.
const FIELD_TO_FORM = {
  title: "title",
  content: "noteBody",
};

export default function CreateNoteModal({
  onClose,
  onSuccess,
  initialCoordinates,
  createNote,
}) {
  // Get default visibility from settings
  const { settings } = useSettings();
  const defaultVisibility = settings?.defaultVisibility || "public";
  const defaultPrecision = settings?.defaultLocationPrecision || "exact";
  const defaultShowAddr = settings?.defaultShowAddress !== false;

  const [title, setTitle] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [visibility, setVisibility] = useState(defaultVisibility);
  const [locationPrecision, setLocationPrecision] = useState(defaultPrecision);
  const [showAddress, setShowAddress] = useState(defaultShowAddr);
  const [images, setImages] = useState([]); // { blob: Blob, previewUrl: string }[]
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [cropFiles, setCropFiles] = useState(null); // File[] | null
  const [selectedCandidateIdx, setSelectedCandidateIdx] = useState(0);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);
  const modalCardRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const addressAnchorRef = useRef(null);

  const {
    displayAddress,
    properties: addressProperties,
    candidates: addressCandidates,
    loading: addressLoading,
    error: addressError,
  } = useReverseGeocode(
    initialCoordinates?.latitude,
    initialCoordinates?.longitude,
  );

  // Reset selection when the candidate list changes, or when toggling
  // precision changes which entries dedupe away.
  useEffect(() => {
    setSelectedCandidateIdx(0);
  }, [addressCandidates, locationPrecision]);

  const labelFor = (candidate) =>
    locationPrecision === "approximate"
      ? formatAddressForApproximateDisplay(candidate.properties)
      : candidate.displayAddress;

  // Dedupe candidates by their currently-rendered label so the picker doesn't
  // show visual duplicates (common when "Approximate" collapses many nearby
  // street addresses to the same "City, ST" string).
  const visibleCandidates = (() => {
    const seen = new Set();
    return addressCandidates.filter((candidate) => {
      const label = labelFor(candidate);
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    });
  })();

  const selectedCandidate =
    visibleCandidates[selectedCandidateIdx] || visibleCandidates[0];
  const selectedDisplayAddress = selectedCandidate
    ? labelFor(selectedCandidate)
    : locationPrecision === "approximate"
      ? formatAddressForApproximateDisplay(addressProperties)
      : displayAddress;

  const maxImages = MAX_NOTE_IMAGES;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const remaining = maxImages - images.length;
      if (remaining <= 0) return;
      setCropFiles(files.slice(0, remaining));
    }
    e.target.value = null;
  };

  const handleCropComplete = (blobs) => {
    const withPreviews = blobs.map((blob) => ({
      blob,
      previewUrl: URL.createObjectURL(blob),
    }));
    setImages((prev) => [...prev, ...withPreviews]);
    setCropFiles(null);
  };

  const handleCropCancel = () => {
    setCropFiles(null);
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const getErrorMessage = (error) => {
    if (error instanceof NetworkError) {
      return "Unable to upload note. Please check your internet connection.";
    }
    if (error instanceof ValidationError) {
      return error.getUserMessage();
    }
    if (error instanceof ServerError) {
      return "Something went wrong. Please try again.";
    }
    if (error.message?.includes("timeout")) {
      return "Upload timed out. Try again with smaller images.";
    }
    return "Failed to create note. Please try again.";
  };

  const handleSave = async () => {
    let formErrors = {};
    if (!title.trim()) formErrors.title = "Please fill out this field.";
    if (!noteBody.trim()) formErrors.noteBody = "Please fill out this field.";

    setErrors(formErrors);
    setSubmitError(null);

    if (Object.keys(formErrors).length === 0) {
      setSubmitting(true);

      try {
        const newNote = await createNote({
          title,
          body: noteBody,
          visibility,
          images: images.map((img) => img.blob),
          address: selectedCandidate?.fullAddress || "Unknown Location",
          latitude: initialCoordinates?.latitude,
          longitude: initialCoordinates?.longitude,
          locationPrecision,
          showAddress,
        });

        onSuccess(newNote);
        onClose();
      } catch (err) {
        logError("Failed to create note:", err);
        const normalizedError = normalizeError(err);

        if (normalizedError instanceof ValidationError) {
          const byField = normalizedError.getErrorsByField();
          const fieldErrors = {};
          const unmapped = [];

          Object.entries(byField).forEach(([backendField, messages]) => {
            const formField = FIELD_TO_FORM[backendField];
            if (formField) {
              fieldErrors[formField] = messages[0];
            } else {
              unmapped.push(messages[0]);
            }
          });

          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
          }

          // If there are unmapped field errors (e.g. visibility, coordinates)
          // show them in the banner. If the ValidationError had no per-field
          // details at all, fall back to the generic getErrorMessage path.
          if (unmapped.length > 0) {
            setSubmitError(unmapped.join(" "));
          } else if (Object.keys(fieldErrors).length === 0) {
            setSubmitError(getErrorMessage(normalizedError));
          }
        } else {
          setSubmitError(getErrorMessage(normalizedError));
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  const isCropping = cropFiles !== null;

  const handleClose = () => {
    if (submitting || isCropping) return;

    images.forEach((img) => {
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
    });
    setImages([]);
    setTitle("");
    setNoteBody("");
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  const modal = (
    <div
      className="fixed inset-0 bg-black/50 flex justify-center items-center z-[60] p-4"
      onClick={submitting || isCropping ? undefined : handleClose}
    >
      <div
        ref={modalCardRef}
        className="bg-surface-elevated rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col relative border border-border-light"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="px-6 pt-6 pb-4 border-b border-border-strong">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="absolute top-4 right-4 text-text-muted hover:text-text-secondary transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          <h2 className="text-2xl font-bold text-text-primary">
            Create a New Note
          </h2>
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-6 py-5 pretty-scroll"
        >
          {submitError && (
            <ErrorBanner
              message={submitError}
              onRetry={handleSave}
              onDismiss={() => setSubmitError(null)}
              className="mb-4"
            />
          )}

          <div className="mb-5">
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Title
            </label>
            <input
              type="text"
              disabled={submitting}
              className={`w-full bg-surface-elevated border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 transition disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.title
                  ? "border-border-destructive focus:ring-red-300"
                  : "border-border-strong focus:ring-primary-300"
              }`}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setSubmitError(null);
              }}
            />
            {errors.title && (
              <p className="text-text-destructive text-xs mt-1">
                {errors.title}
              </p>
            )}
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Note
            </label>
            <textarea
              disabled={submitting}
              className={`w-full bg-surface-elevated border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 transition resize-none disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.noteBody
                  ? "border-border-destructive focus:ring-red-300"
                  : "border-border-strong focus:ring-primary-300"
              }`}
              rows="5"
              value={noteBody}
              onChange={(e) => {
                setNoteBody(e.target.value);
                setSubmitError(null);
              }}
            ></textarea>
            {errors.noteBody && (
              <p className="text-text-destructive text-xs mt-1">
                {errors.noteBody}
              </p>
            )}
          </div>

          <div className="mb-5 flex items-center gap-3">
            <span className="text-sm font-medium text-text-secondary">
              Visibility:
            </span>
            <VisibilityToggle
              value={visibility}
              onChange={setVisibility}
              size="md"
              disabled={submitting}
            />
          </div>
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Location
            </label>
            <div className="flex items-center gap-2">
              <MapPinIcon className="h-4 w-4 text-text-muted flex-shrink-0" />
              {addressLoading ? (
                <div className="flex items-center gap-2">
                  <Spinner size={14} />
                  <span className="text-xs text-text-muted">
                    Fetching location...
                  </span>
                </div>
              ) : addressError || visibleCandidates.length === 0 ? (
                <span className="text-xs text-text-muted">
                  Unknown Location
                </span>
              ) : visibleCandidates.length === 1 ? (
                <span className="text-sm text-text-secondary">
                  {selectedDisplayAddress}
                </span>
              ) : (
                <button
                  ref={addressAnchorRef}
                  type="button"
                  disabled={submitting}
                  onClick={() => setAddressDropdownOpen((open) => !open)}
                  className="flex items-center gap-1 text-sm text-text-secondary rounded-md px-1.5 py-0.5 -mx-1.5 hover:bg-surface-sunken focus:outline-none focus:bg-surface-sunken disabled:opacity-50 disabled:cursor-not-allowed transition"
                  aria-haspopup="menu"
                  aria-expanded={addressDropdownOpen}
                >
                  <span className="text-left">{selectedDisplayAddress}</span>
                  <ChevronDownIcon className="h-4 w-4 text-text-muted flex-shrink-0" />
                </button>
              )}
            </div>
            {addressDropdownOpen && visibleCandidates.length > 1 && (
              <DropdownMenuPortal
                anchorRef={addressAnchorRef}
                boundaryRef={scrollContainerRef}
                scrollRefs={[scrollContainerRef]}
                onClose={() => setAddressDropdownOpen(false)}
                side="bottom"
                align="left"
                widthClass="w-72"
                maxHeight={240}
                items={visibleCandidates.map((candidate, idx) => ({
                  label: labelFor(candidate),
                  icon:
                    idx === selectedCandidateIdx ? (
                      <CheckIcon className="h-4 w-4 text-primary flex-shrink-0" />
                    ) : (
                      <span className="h-4 w-4 flex-shrink-0" />
                    ),
                  onSelect: () => setSelectedCandidateIdx(idx),
                }))}
              />
            )}
          </div>

          <div className="mb-5 flex items-center gap-3">
            <span className="text-sm font-medium text-text-secondary">
              Precision:
            </span>
            <LocationPrecisionToggle
              value={locationPrecision}
              onChange={setLocationPrecision}
              size="md"
              disabled={submitting}
              units={settings?.units || "miles"}
            />
          </div>

          <div className="mb-5 flex items-center justify-between">
            <span className="text-sm font-medium text-text-secondary">
              Show address on note
            </span>
            <ToggleSwitch
              id="show-address-toggle"
              checked={showAddress}
              onChange={setShowAddress}
              ariaLabel="Toggle address visibility on note"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Upload Images
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={submitting || isCropping}
                onClick={() => {
                  if (images.length > 0) {
                    setCropFiles([]);
                  } else {
                    fileInputRef.current.click();
                  }
                }}
                className="file:mr-4 file:py-2 file:px-4 py-2 px-4 rounded-2xl border border-border-strong font-semibold bg-primary text-text-inverse hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {images.length > 0 ? "Edit images" : "Add images"}
              </button>
              {images.length > 0 && (
                <span className="text-sm text-text-secondary">
                  {images.length} image{images.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
            />
            {images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className="w-10 h-[53px] rounded-md overflow-hidden ring-1 ring-border"
                  >
                    <img
                      src={img.previewUrl}
                      alt="preview"
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="px-6 py-4 border-t border-border-strong flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={submitting || isCropping}
            className="px-5 py-2 bg-surface-sunken text-text-secondary rounded-lg hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || isCropping}
            className="px-5 py-2 bg-primary text-text-inverse rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Spinner size={16} />}
            {submitting ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modal, document.body)}
      {cropFiles && (
        <ImageCropModal
          initialFiles={cropFiles}
          onSave={handleCropComplete}
          onClose={handleCropCancel}
          existingImageCount={images.length}
          existingImagePreviews={images.map((img) => img.previewUrl)}
          onRemoveExisting={(index) => handleRemoveImage(index)}
        />
      )}
    </>
  );
}
